import React, { useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useUsers } from "../../hooks/useUsers";
import { useQuery } from "@tanstack/react-query";
import CircleCheckBig from "./icons/CircleCheckBig";
import LoadingButton from "../LoadingButton";
import { Minus } from "lucide-react";

const UserDropdownModal = ({
  isOpen,
  onClose,
  selectedUserId,
  onSelect,
  submissionLoading,
  handleAssignUserToRole,
  viewMode = false,
  assignedUsers = [],
  removeRole,
}) => {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const { fetchUsers } = useUsers({
    filters: { user_type: "sub_admin" },
  });

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
    keepPreviousData: true,
    enabled: !!isOpen,
  });
  useEffect(() => {
    if (!usersQuery.isLoading && usersQuery.data) {
      const response = usersQuery.data;
      setUsers(response?.data?.users || []);
    }
  }, [usersQuery.data]);

  const filtered = users.filter((u) =>
    u?.full_name?.toLowerCase().includes(search?.toLowerCase())
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {!viewMode ? "Select User" : "Assigned Users"}
                </Dialog.Title>
                {!viewMode && (
                  <div className="mt-4">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div className="mt-4 max-h-60 overflow-y-auto">
                  {viewMode ? (
                    filtered.length === 0 ? (
                      <p className="text-sm text-gray-500">No users found.</p>
                    ) : (
                      assignedUsers.map((user) => (
                        <div
                          key={user._id}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-md mb-1 bg-blue-100"
                        >
                          <span className="flex items-center gap-2 text-left">
                            {user.full_name}
                            <CircleCheckBig className="h-4 w-4 text-blue-500" />
                          </span>
                          <button
                            onClick={() => {
                              removeRole(user._id);
                            }}
                          >
                            <Minus />
                          </button>
                        </div>
                      ))
                    )
                  ) : filtered.length === 0 ? (
                    <p className="text-sm text-gray-500">No users found.</p>
                  ) : (
                    filtered.map((user) => (
                      <button
                        key={user._id}
                        onClick={() => onSelect(user)}
                        className={`w-full text-left px-3 py-2 rounded-md mb-1 flex items-center justify-between 
                        ${
                          user._id === selectedUserId
                            ? "bg-blue-100"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        <span>{user.full_name}</span>
                        {user._id === selectedUserId && (
                          <CircleCheckBig className="h-4 w-4 text-blue-500" />
                        )}
                      </button>
                    ))
                  )}
                </div>
                <div className="px-6 py-4 border-t flex justify-end space-x-3">
                  <button
                    type="button"
                    className="px-4 py-2 border rounded-md text-gray-700 bg-white hover:bg-gray-100"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  {!viewMode && (
                    <button
                      onClick={() => handleAssignUserToRole()}
                      disabled={submissionLoading}
                      className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
                    >
                      {submissionLoading ? <LoadingButton /> : <>Save</>}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserDropdownModal;
